import type { HelpArticle } from '../types';

export const PRIVACY_ARTICLES: HelpArticle[] = [
    {
        slug: 'como-protegemos-os-dados',
        topicId: 'privacy',
        title: 'Como o LaudoPsi protege os dados dos seus pacientes',
        description:
            'Criptografia, links com expiração e as práticas que mantêm as informações clínicas em segurança.',
        updatedAt: '2026-09-07',
        keywords: ['segurança', 'lgpd', 'criptografia', 'dados', 'proteção', 'privacidade'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Dados de avaliação psicológica são dados sensíveis, e tratamos essa responsabilidade com seriedade. Este guia resume as principais camadas de proteção da plataforma.',
            },
            { type: 'subheading', id: 'criptografia', text: 'Criptografia dos dados' },
            {
                type: 'paragraph',
                text: 'Os dados pessoais dos pacientes, como o nome, são **criptografados no nosso banco de dados**. O acesso às informações exige a sua autenticação, e todo o tráfego entre o seu navegador e a plataforma é criptografado.',
            },
            { type: 'subheading', id: 'links', text: 'Links com expiração' },
            {
                type: 'paragraph',
                text: 'Todo link enviado a paciente, seja de teste, formulário ou pré-anamnese, é um link seguro com **validade de 30 dias**. Depois disso ele expira e deixa de dar acesso ao conteúdo.',
            },
            { type: 'subheading', id: 'ia', text: 'IA com desidentificação' },
            {
                type: 'paragraph',
                text: 'Antes de qualquer texto ser processado por inteligência artificial, os dados que identificam o paciente são removidos. O guia **IA e sigilo profissional** explica esse processo em detalhes.',
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'Quer os detalhes formais? Consulte a Política de Privacidade e os Termos de Uso, disponíveis no rodapé do site e nas páginas legais da plataforma.',
            },
        ],
    },
    {
        slug: 'ia-e-sigilo-profissional',
        topicId: 'privacy',
        title: 'IA e sigilo profissional: o que é enviado e o que não é',
        description:
            'Como a desidentificação funciona e por que os provedores de IA nunca veem os dados do seu paciente.',
        updatedAt: '2026-07-03',
        keywords: ['ia', 'sigilo', 'desidentificação', 'pii', 'confidencialidade', 'ética'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Usar IA em documentos clínicos levanta uma pergunta justa: para onde vão os dados do meu paciente? No LaudoPsi, a resposta é direta. Os dados que identificam o paciente **não saem da plataforma**.',
            },
            { type: 'subheading', id: 'como-funciona', text: 'Como funciona a desidentificação' },
            {
                type: 'steps',
                items: [
                    'Antes do envio para o serviço de IA, as informações que identificam o paciente, como nome e dados de contato, são removidas do texto.',
                    'A IA processa apenas o conteúdo desidentificado e devolve o rascunho.',
                    'O LaudoPsi recoloca os dados do paciente no texto final, de forma segura, já dentro da plataforma.',
                ],
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'Na prática: o provedor de IA nunca tem acesso à identidade do seu paciente. Ele vê o caso clínico, não a pessoa.',
            },
            { type: 'subheading', id: 'seu-papel', text: 'O seu papel como profissional' },
            {
                type: 'paragraph',
                text: 'A IA gera rascunhos, mas o documento é seu. Revise cada texto gerado, ajuste o que for necessário e lembre que o diagnóstico e as conclusões clínicas são decisões suas, amparadas pelo seu registro profissional.',
            },
            {
                type: 'paragraph',
                text: 'No editor, o link **Saiba como protegemos seus dados**, exibido junto ao botão de gerar com IA, leva a essas informações sempre que você precisar.',
            },
        ],
    },
    {
        slug: 'links-enviados-a-pacientes',
        topicId: 'privacy',
        title: 'Como funcionam os links enviados a pacientes',
        description:
            'Validade de 30 dias, o que o paciente vê ao abrir e boas práticas na hora de compartilhar.',
        updatedAt: '2026-09-07',
        keywords: ['link', 'expiração', '30 dias', 'compartilhar', 'whatsapp', 'segurança'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Testes, formulários e pré-anamneses chegam ao paciente por meio de um link. Entender como ele funciona ajuda a orientar o paciente e a manter o processo seguro.',
            },
            { type: 'subheading', id: 'validade', text: 'Validade de 30 dias' },
            {
                type: 'paragraph',
                text: 'Cada link é único, gerado para um paciente e um instrumento específicos, e **expira em 30 dias**. Se o paciente não responder a tempo, gere um novo link atribuindo o instrumento novamente. Nada se perde.',
            },
            { type: 'subheading', id: 'o-que-o-paciente-ve', text: 'O que o paciente vê' },
            {
                type: 'paragraph',
                text: 'Ao abrir o link, o paciente encontra apenas o questionário que você enviou, em uma página simples que funciona no celular. Ele não tem acesso à plataforma, aos seus laudos nem a dados de outros pacientes.',
            },
            { type: 'subheading', id: 'boas-praticas', text: 'Boas práticas ao compartilhar' },
            {
                type: 'list',
                items: [
                    'Envie o link direto para o contato do paciente ou do responsável, de preferência pelo canal que vocês já usam, como o WhatsApp.',
                    'Avise o paciente de que o link vence em 30 dias e combine quando ele pretende responder.',
                    'Evite publicar o link em grupos ou redes sociais. Ele é individual.',
                ],
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'Respostas recebidas ficam guardadas na página do paciente, mesmo depois que o link expira.',
            },
        ],
    },
];
